/* eslint-disable prettier/prettier */
import { useState, useCallback, useMemo } from 'react';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';

moment.locale('pt-br');

export type PeriodType =
  | 'month'
  | 'quarter'
  | 'semester'
  | 'year'
  | 'custom'
  | 'current_year'
  | 'last_year';

export interface FilterFormValues {
  companies?: { external_id: string };
  cidade?: { value: string };
  uf?: { value: string };
  grupo_de_empresas?: { value: string }[];
  tags?: { value: string };
  comercial?: { value: string };
  contabil?: { value: string };
  financeiro?: { value: string };
  contas_a_pagar?: { value: string };
  encerramento_anual?: { value: string };
  fiscal?: { value: string };
  imposto_de_renda?: { value: string };
  integracao_contabil?: { value: string };
  integracao_fiscal?: { value: string };
  pessoal?: { value: string };
  relacionamento_com_cliente?: { value: string };
  societario?: { value: string };
}

interface UseTaxFiltersOptions {
  screenData: 'fiscal' | 'simples_nacional';
}

interface UseTaxFiltersReturn {
  // Estado
  filters: URLSearchParams;
  currentFilterDate: moment.Moment;
  selectedPeriod: PeriodType;
  useCustomRange: boolean;
  customDateStart: string;
  customDateEnd: string;

  // Handlers de período
  handlePrevMonth: () => void;
  handleNextMonth: () => void;
  // eslint-disable-next-line no-unused-vars
  handleSelectMonth: (year: number, month: number) => void;
  handleQuarterPeriod: () => void;
  handleSemesterPeriod: () => void;
  handleYearPeriod: () => void;
  handleCurrentYearPeriod: () => void;
  handleLastYearPeriod: () => void;
  handleApplyCustomRange: () => void;
  handleResetToMonth: () => void;
  // eslint-disable-next-line no-unused-vars
  setCustomDateStart: (date: string) => void;
  // eslint-disable-next-line no-unused-vars
  setCustomDateEnd: (date: string) => void;

  // Handlers de filtros avançados
  activeFiltersCount: number;
  // eslint-disable-next-line no-unused-vars
  handleApplyFilters: (values: FilterFormValues, tipoRegime?: string) => void;
  handleClearFilters: () => void;
  // eslint-disable-next-line no-unused-vars
  handleSearchChange: (search: string, searchType?: string) => void;
  // eslint-disable-next-line no-unused-vars
  handleRegimeChange: (regime: string) => void;
  // eslint-disable-next-line no-unused-vars
  handleSort: (field: string) => void;
  orderBy: string;
  orderDirection: 'asc' | 'desc';
}

export function useTaxFilters(options: UseTaxFiltersOptions): UseTaxFiltersReturn {
  const { screenData } = options;

  const [useCustomRange, setUseCustomRange] = useState(false);
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('month');

  // Usar mês anterior como default
  const previousMonth = moment().subtract(1, 'month');

  const [filters, setFilters] = useSearchParams(
    createSearchParams({
      page: '1',
      size: '50',
      screen_data: screenData,
      search: '',
      date_start: previousMonth.startOf('month').format('YYYY-MM-DD'),
      date_end: previousMonth.clone().endOf('month').format('YYYY-MM-DD'),
    }),
  );

  const currentFilterDate = useMemo(() => {
    return moment(filters.get('date_start') ?? moment().format('YYYY-MM-DD'));
  }, [filters]);

  // Handlers de navegação mensal
  const handlePrevMonth = useCallback(() => {
    const prevMonth = currentFilterDate.clone().subtract(1, 'month');
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: prevMonth.startOf('month').format('YYYY-MM-DD'),
        date_end: prevMonth.endOf('month').format('YYYY-MM-DD'),
        page: '1',
      }),
    );
    setUseCustomRange(false);
    setSelectedPeriod('month');
  }, [currentFilterDate, filters, setFilters]);

  const handleNextMonth = useCallback(() => {
    const nextMonth = currentFilterDate.clone().add(1, 'month');
    if (!nextMonth.isAfter(moment(), 'month')) {
      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: nextMonth.startOf('month').format('YYYY-MM-DD'),
          date_end: nextMonth.endOf('month').format('YYYY-MM-DD'),
          page: '1',
        }),
      );
      setUseCustomRange(false);
      setSelectedPeriod('month');
    }
  }, [currentFilterDate, filters, setFilters]);

  const handleSelectMonth = useCallback(
    (year: number, month: number) => {
      const selectedDate = moment().year(year).month(month);
      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: selectedDate.startOf('month').format('YYYY-MM-DD'),
          date_end: selectedDate.endOf('month').format('YYYY-MM-DD'),
          page: '1',
        }),
      );
      setUseCustomRange(false);
      setSelectedPeriod('month');
    },
    [filters, setFilters],
  );

  // Handlers de período rápido
  const handleQuarterPeriod = useCallback(() => {
    const endDate = moment();
    const startDate = moment().subtract(3, 'months').startOf('month');
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: startDate.format('YYYY-MM-DD'),
        date_end: endDate.format('YYYY-MM-DD'),
        page: '1',
      }),
    );
    setUseCustomRange(false);
    setSelectedPeriod('quarter');
  }, [filters, setFilters]);

  const handleSemesterPeriod = useCallback(() => {
    const endDate = moment();
    const startDate = moment().subtract(6, 'months').startOf('month');
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: startDate.format('YYYY-MM-DD'),
        date_end: endDate.format('YYYY-MM-DD'),
        page: '1',
      }),
    );
    setUseCustomRange(false);
    setSelectedPeriod('semester');
  }, [filters, setFilters]);

  const handleYearPeriod = useCallback(() => {
    const endDate = moment();
    const startDate = moment().subtract(12, 'months').startOf('month');
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: startDate.format('YYYY-MM-DD'),
        date_end: endDate.format('YYYY-MM-DD'),
        page: '1',
      }),
    );
    setUseCustomRange(false);
    setSelectedPeriod('year');
  }, [filters, setFilters]);

  const handleCurrentYearPeriod = useCallback(() => {
    const startDate = moment().startOf('year');
    const endDate = moment().endOf('year');
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: startDate.format('YYYY-MM-DD'),
        date_end: endDate.format('YYYY-MM-DD'),
        page: '1',
      }),
    );
    setUseCustomRange(false);
    setSelectedPeriod('current_year');
  }, [filters, setFilters]);

  const handleLastYearPeriod = useCallback(() => {
    const startDate = moment().subtract(1, 'year').startOf('year');
    const endDate = moment().subtract(1, 'year').endOf('year');
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: startDate.format('YYYY-MM-DD'),
        date_end: endDate.format('YYYY-MM-DD'),
        page: '1',
      }),
    );
    setUseCustomRange(false);
    setSelectedPeriod('last_year');
  }, [filters, setFilters]);

  // Handler de range personalizado
  const handleApplyCustomRange = useCallback(() => {
    if (customDateStart && customDateEnd) {
      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: customDateStart,
          date_end: customDateEnd,
          page: '1',
        }),
      );
      setUseCustomRange(true);
      setSelectedPeriod('custom');
    }
  }, [customDateStart, customDateEnd, filters, setFilters]);

  // Handler para resetar para o mês atual
  const handleResetToMonth = useCallback(() => {
    const currentMonth = moment();
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: currentMonth.startOf('month').format('YYYY-MM-DD'),
        date_end: currentMonth.endOf('month').format('YYYY-MM-DD'),
        page: '1',
      }),
    );
    setUseCustomRange(false);
    setCustomDateStart('');
    setCustomDateEnd('');
    setSelectedPeriod('month');
  }, [filters, setFilters]);

  // Contagem de filtros ativos
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.get('external_id')) count += 1;
    if (filters.get('cidade')) count += 1;
    if (filters.get('uf')) count += 1;
    // Conta quantos grupos de empresas estão selecionados
    const grupoDeEmpresas = filters.get('grupo_de_empresas');
    if (grupoDeEmpresas) {
      const grupos = grupoDeEmpresas.split(',').filter(g => g.trim());
      count += grupos.length;
    }
    if (filters.get('tags')) count += 1;
    if (filters.get('comercial')) count += 1;
    if (filters.get('contabil')) count += 1;
    if (filters.get('financeiro')) count += 1;
    if (filters.get('contas_a_pagar')) count += 1;
    if (filters.get('encerramento_anual')) count += 1;
    if (filters.get('fiscal')) count += 1;
    if (filters.get('imposto_de_renda')) count += 1;
    if (filters.get('integracao_contabil')) count += 1;
    if (filters.get('integracao_fiscal')) count += 1;
    if (filters.get('pessoal')) count += 1;
    if (filters.get('relacionamento_com_cliente')) count += 1;
    if (filters.get('societario')) count += 1;
    return count;
  }, [filters]);

  // Handler para aplicar filtros avançados
  const handleApplyFilters = useCallback(
    (values: FilterFormValues, tipoRegimeParam?: string) => {
      const newFilters = new URLSearchParams();

      newFilters.set('page', '1');
      newFilters.set('size', '50');
      newFilters.set('search', filters.get('search') || '');
      newFilters.set('screen_data', screenData);
      newFilters.set(
        'date_start',
        filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD'),
      );
      newFilters.set(
        'date_end',
        filters.get('date_end') || moment().endOf('month').format('YYYY-MM-DD'),
      );

      // Usa o parâmetro passado, se não usa o valor atual dos filtros
      const tipoRegime =
        tipoRegimeParam !== undefined ? tipoRegimeParam : filters.get('tipo_regime');
      if (tipoRegime) newFilters.set('tipo_regime', tipoRegime);

      if (values.companies?.external_id)
        newFilters.set('external_id', values.companies.external_id);
      if (values.cidade?.value) newFilters.set('cidade', values.cidade.value);
      if (values.uf?.value) newFilters.set('uf', values.uf.value);
      if (values.grupo_de_empresas && values.grupo_de_empresas.length > 0) {
        const gruposValues = values.grupo_de_empresas.map(g => g.value).join(',');
        newFilters.set('grupo_de_empresas', gruposValues);
      }
      if (values.tags?.value) newFilters.set('tags', values.tags.value);
      if (values.comercial?.value) newFilters.set('comercial', values.comercial.value);
      if (values.contabil?.value) newFilters.set('contabil', values.contabil.value);
      if (values.financeiro?.value) newFilters.set('financeiro', values.financeiro.value);
      if (values.contas_a_pagar?.value)
        newFilters.set('contas_a_pagar', values.contas_a_pagar.value);
      if (values.encerramento_anual?.value)
        newFilters.set('encerramento_anual', values.encerramento_anual.value);
      if (values.fiscal?.value) newFilters.set('fiscal', values.fiscal.value);
      if (values.imposto_de_renda?.value)
        newFilters.set('imposto_de_renda', values.imposto_de_renda.value);
      if (values.integracao_contabil?.value)
        newFilters.set('integracao_contabil', values.integracao_contabil.value);
      if (values.integracao_fiscal?.value)
        newFilters.set('integracao_fiscal', values.integracao_fiscal.value);
      if (values.pessoal?.value) newFilters.set('pessoal', values.pessoal.value);
      if (values.relacionamento_com_cliente?.value)
        newFilters.set('relacionamento_com_cliente', values.relacionamento_com_cliente.value);
      if (values.societario?.value) newFilters.set('societario', values.societario.value);

      setFilters(newFilters);
    },
    [filters, screenData, setFilters],
  );

  // Handler para limpar filtros avançados
  const handleClearFilters = useCallback(() => {
    const clearedFilters = createSearchParams({
      page: '1',
      size: '50',
      screen_data: screenData,
      search: filters.get('search') || '',
      date_start: filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD'),
      date_end: filters.get('date_end') || moment().endOf('month').format('YYYY-MM-DD'),
    });

    setFilters(clearedFilters);
  }, [filters, screenData, setFilters]);

  // Handler para busca
  const handleSearchChange = useCallback(
    (search: string, searchType?: string) => {
      const newFilters = new URLSearchParams(filters);
      let trimmedSearch = search.trim();

      // Limpa todos os parâmetros de busca antes de definir o correto
      newFilters.delete('search');
      newFilters.delete('external_id');
      newFilters.delete('cnpj');
      newFilters.delete('razao_social');

      if (trimmedSearch && searchType) {
        // Remove pontuação do CNPJ (., -, /)
        if (searchType === 'cnpj') {
          trimmedSearch = trimmedSearch.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
        }
        newFilters.set(searchType, trimmedSearch);
      }

      newFilters.set('page', '1');
      setFilters(newFilters);
    },
    [filters, setFilters],
  );

  // Handler para mudança de regime tributário
  const handleRegimeChange = useCallback(
    (regime: string) => {
      const newFilters = new URLSearchParams(filters);

      if (regime) {
        newFilters.set('tipo_regime', regime);
      } else {
        newFilters.delete('tipo_regime');
      }

      newFilters.set('page', '1');
      setFilters(newFilters);
    },
    [filters, setFilters],
  );

  // Extrair ordenação atual dos filtros
  const orderByParam = filters.get('order_by') || '';
  const orderBy = orderByParam.replace(/_asc$|_desc$/, '');
  const orderDirection: 'asc' | 'desc' = orderByParam.endsWith('_desc') ? 'desc' : 'asc';

  // Handler para ordenação
  const handleSort = useCallback(
    (field: string) => {
      const currentOrderBy = filters.get('order_by') || '';
      const currentField = currentOrderBy.replace(/_asc$|_desc$/, '');
      const currentDir = currentOrderBy.endsWith('_desc') ? 'desc' : 'asc';

      let newDirection: 'asc' | 'desc' = 'asc';
      if (currentField === field) {
        newDirection = currentDir === 'asc' ? 'desc' : 'asc';
      }

      const newFilters = new URLSearchParams(filters);
      newFilters.set('order_by', `${field}_${newDirection}`);
      newFilters.set('page', '1');
      setFilters(newFilters);
    },
    [filters, setFilters],
  );

  return {
    // Estado
    filters,
    currentFilterDate,
    selectedPeriod,
    useCustomRange,
    customDateStart,
    customDateEnd,

    // Handlers de período
    handlePrevMonth,
    handleNextMonth,
    handleSelectMonth,
    handleQuarterPeriod,
    handleSemesterPeriod,
    handleYearPeriod,
    handleCurrentYearPeriod,
    handleLastYearPeriod,
    handleApplyCustomRange,
    handleResetToMonth,
    setCustomDateStart,
    setCustomDateEnd,

    // Handlers de filtros avançados
    activeFiltersCount,
    handleApplyFilters,
    handleClearFilters,
    handleSearchChange,
    handleRegimeChange,

    // Ordenação
    handleSort,
    orderBy,
    orderDirection,
  };
}
